/**
 * Why a seat has no price. Rendering "$0" for any of these would tell the customer
 * the seat is free, so every one of them is a distinct, explainable state rather
 * than a number.
 */
export enum eSeatQuoteUnavailable {
  /** The subscription is not in a state that can buy (churned, past due, no plan). */
  SUBSCRIPTION_NOT_ELIGIBLE = 'SUBSCRIPTION_NOT_ELIGIBLE',
  /** The plan legitimately sells no SEATS product. */
  NO_SEATS_PRODUCT = 'NO_SEATS_PRODUCT',
  /** The products catalogue could not be read. */
  PRODUCTS_UNAVAILABLE = 'PRODUCTS_UNAVAILABLE',
}

/**
 * What one seat costs, recurring. Either a real price with the product it resolves
 * to, or an explicit refusal to quote — never a zero standing in for both.
 */
export type tSeatQuote =
  | {
      available: true;
      /** Recurring per-seat amount, in cents, in `currency`. */
      recurringAmountCents: number;
      currency: string;
      /** The product the purchase must buy, so quote and charge cannot diverge. */
      productId: string;
      /**
       * Whether this quote can actually be bought through the inline flow.
       *
       * A legacy plan carries no SEATS product: its price is real and worth showing,
       * but there is nothing to purchase, so the flow must route to the Subscription
       * tab instead of offering a submit that is guaranteed to fail.
       */
      purchasable: boolean;
    }
  | { available: false; reason: eSeatQuoteUnavailable };

/**
 * The amount that will actually be charged today, as returned by the server's
 * invoice preview. Never computed here: `always_invoice` prorations fold in item
 * amounts, coupons, tax and rounding, so any figure derived in the client drifts
 * from the invoice the customer is really sent.
 *
 * `known: false` means the preview is unavailable or the flow defers billing
 * (trial, invoice terms). It does not mean zero.
 */
export type tAmountDueToday =
  | { known: true; amountCents: number; currency: string }
  | { known: false };

/**
 * The result of trying to buy a seat. Deliberately explicit rather than a truthy
 * response: the branch this replaces returned early on `result?.error` without
 * throwing and without clearing its in-flight flag, and fell through to the success
 * path when `requires_action` arrived with no client secret. U6 sequences the invite
 * on this contract, so "did the money move?" must be answerable from the value.
 */
export type tSeatPurchaseOutcome =
  | { status: 'purchased' }
  | {
      status: 'failed';
      message: string;
      errorCode?: string;
      /**
       * Whether the bank declined, decided HERE against the raw error payload.
       *
       * The caller must not re-derive it: by the time the failure leaves this hook the
       * error has been flattened to a message and a single code, and Stripe's decline
       * signal lives in fields that flattening drops (`decline_code`, the structured
       * `payment_declined` flag). Only a decline earns "try again" or "use a different
       * card" — offering those on a duplicate-in-flight or a server error would be a
       * lie.
       */
      declined: boolean;
    };

export type tSeatPurchase = {
  /**
   * Whether the quote can be trusted. Folds subscription loading, the provider's
   * `hasSubscriptionData`, and the hook's own products fetch. A money flow must not
   * quote while any of those are unsettled — the subscription object still holds the
   * PREVIOUS workspace's values during a switch.
   */
  isReady: boolean;
  quote: tSeatQuote;
  amountDueToday: tAmountDueToday;
  isLoadingAmountDueToday: boolean;
  isPurchasing: boolean;
  /**
   * Buy exactly one seat. Mints a fresh purchase intent per attempt and rotates it on
   * every terminal outcome, so two deliberate purchases in one session produce two
   * charges instead of a silently replayed idempotent 200.
   */
  purchaseOneSeat: () => Promise<tSeatPurchaseOutcome>;
  /** Discards the current intent — call when the flow closes without buying. */
  resetPurchaseIntent: () => void;
};
